import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../constants/routes';
import SearchService from '../services/SearchService';
import { parseNLPCommand, getCommandSuggestions } from '../utils/nlpCommandService';

const SearchContext = createContext(null);

// Storage keys
const SEARCH_HISTORY_KEY = 'bha_search_history';
const RECENT_PAGES_KEY = 'bha_recent_pages';
const QUICK_NOTES_KEY = 'bha_quick_notes';

// HIPAA: Use sessionStorage for search history & notes (may contain patient names/PHI).
// Data is cleared automatically when the browser tab closes.
// Recent pages use sessionStorage too for consistency (no PHI but good practice).
const phiStorage = typeof window !== 'undefined' ? window.sessionStorage : null;

// Page definitions for search
const PAGES = [
  { key: 'dashboard', label: 'Dashboard', path: ROUTES.ADMIN_DASHBOARD, keywords: ['home', 'main', 'overview', 'start'] },
  { key: 'bookings', label: 'Bookings', path: ROUTES.ADMIN_EVENTS, keywords: ['events', 'appointments', 'calendar', 'schedule'] },
  { key: 'intake', label: 'Intake', path: ROUTES.ADMIN_PATIENT_QUEUE, keywords: ['queue', 'patients', 'waiting', 'check-in'] },
  { key: 'users', label: 'Users', path: ROUTES.ADMIN_USERS, keywords: ['staff', 'team', 'employees', 'accounts'] },
  { key: 'members', label: 'Members', path: ROUTES.ADMIN_ALL_MEMBERS, keywords: ['patients', 'screenings', 'records', 'people'] },
  { key: 'cases', label: 'Outreach', path: ROUTES.ADMIN_CASE_MANAGEMENT, keywords: ['case management', 'outreach', 'follow-up', 'care', 'email', 'text'] },
  { key: 'call-logs', label: 'Call Logs', path: ROUTES.ADMIN_CALL_LOGS, keywords: ['calls', 'phone', 'history', 'voice'] },
  { key: 'volunteers', label: 'Volunteers', path: ROUTES.ADMIN_VOLUNTEER_REGISTRATIONS, keywords: ['volunteer', 'helpers', 'registration'] },
  { key: 'health-outcomes', label: 'Health Outcomes Analytics', path: ROUTES.ADMIN_ANALYTICS_HEALTH_OUTCOMES, keywords: ['analytics', 'health', 'outcomes', 'reports'] },
  { key: 'insurance', label: 'Insurance', path: ROUTES.ADMIN_INSURANCE_ENROLLMENTS, keywords: ['insurance', 'enrollment', 'coverage', 'wright-care'] },
  { key: 'operations', label: 'Operations Analytics', path: ROUTES.ADMIN_ANALYTICS_OPERATIONS, keywords: ['analytics', 'operations', 'efficiency'] },
  { key: 'wait-times', label: 'Wait Times Analytics', path: ROUTES.ADMIN_ANALYTICS_WAIT_TIMES, keywords: ['analytics', 'wait', 'time', 'queue'] },
  { key: 'events-analytics', label: 'Events Analytics', path: ROUTES.ADMIN_ANALYTICS_EVENTS, keywords: ['analytics', 'events', 'attendance'] },
  { key: 'booking-analytics', label: 'Booking Analytics', path: ROUTES.ADMIN_ANALYTICS_BOOKING, keywords: ['analytics', 'booking', 'appointments'] },
  { key: 'population', label: 'Population Analytics', path: ROUTES.ADMIN_ANALYTICS_POPULATION, keywords: ['analytics', 'population', 'demographics'] },
  { key: 'volunteer-analytics', label: 'Volunteer Analytics', path: ROUTES.ADMIN_ANALYTICS_VOLUNTEERS, keywords: ['analytics', 'volunteers'] },
  { key: 'audit-logs', label: 'Audit Logs', path: ROUTES.ADMIN_AUDIT_LOGS, keywords: ['logs', 'audit', 'activity', 'history'] },
  { key: 'settings', label: 'Settings', path: ROUTES.SETTINGS, keywords: ['preferences', 'config', 'configuration', 'options'] },
];

// Quick actions
const QUICK_ACTIONS = [
  { key: 'new-event', label: 'Create New Event', action: 'navigate', path: ROUTES.ADMIN_EVENTS, params: { action: 'create' } },
  { key: 'view-queue', label: 'View Patient Queue', action: 'navigate', path: ROUTES.ADMIN_PATIENT_QUEUE },
  { key: 'view-analytics', label: 'View Health Analytics', action: 'navigate', path: ROUTES.ADMIN_ANALYTICS_HEALTH_OUTCOMES },
  { key: 'view-members', label: 'View All Members', action: 'navigate', path: ROUTES.ADMIN_SCREENINGS },
  { key: 'view-calls', label: 'View Recent Calls', action: 'navigate', path: ROUTES.ADMIN_CALL_LOGS },
  { key: 'view-volunteers', label: 'View Volunteers', action: 'navigate', path: ROUTES.ADMIN_VOLUNTEER_REGISTRATIONS },
];

export const SearchProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [isSearching, setIsSearching] = useState(false);
  const [entityResults, setEntityResults] = useState({
    members: [],
    bookings: [],
    volunteers: [],
    calls: []
  });
  const [searchHistory, setSearchHistory] = useState([]);
  const [recentPages, setRecentPages] = useState([]);
  const [quickNotes, setQuickNotes] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [parsedCommand, setParsedCommand] = useState(null);
  const [commandSuggestions, setCommandSuggestions] = useState([]);
  
  const navigate = useNavigate();
  const location = useLocation();
  const searchTimeoutRef = useRef(null);

  // Load persisted data on mount
  useEffect(() => {
    try {
      const savedHistory = phiStorage?.getItem(SEARCH_HISTORY_KEY);
      const savedPages = phiStorage?.getItem(RECENT_PAGES_KEY);
      const savedNotes = phiStorage?.getItem(QUICK_NOTES_KEY);
      
      if (savedHistory) setSearchHistory(JSON.parse(savedHistory));
      if (savedPages) setRecentPages(JSON.parse(savedPages));
      if (savedNotes) setQuickNotes(JSON.parse(savedNotes));
    } catch (e) {
      console.error('Error loading search data from sessionStorage:', e);
    }
  }, []);

  // Track recent pages
  useEffect(() => {
    const currentPath = location.pathname;
    const page = PAGES.find(p => p.path === currentPath);
    
    if (page) {
      setRecentPages(prev => {
        const filtered = prev.filter(p => p.path !== currentPath);
        const updated = [{ ...page, visitedAt: Date.now() }, ...filtered].slice(0, 5);
        phiStorage?.setItem(RECENT_PAGES_KEY, JSON.stringify(updated));
        return updated;
      });
    }
  }, [location.pathname]);

  // Keyboard shortcut listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd/Ctrl + K to toggle
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
      
      // Escape to close
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        closeSearch();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Search pages locally
  const searchPages = useCallback((searchQuery) => {
    if (!searchQuery.trim()) return [];
    
    const lowerQuery = searchQuery.toLowerCase();
    
    return PAGES
      .map(page => {
        let score = 0;
        const lowerLabel = page.label.toLowerCase();
        const lowerKey = page.key.toLowerCase();
        
        // Exact match
        if (lowerLabel === lowerQuery || lowerKey === lowerQuery) score = 100;
        // Starts with
        else if (lowerLabel.startsWith(lowerQuery) || lowerKey.startsWith(lowerQuery)) score = 80;
        // Contains
        else if (lowerLabel.includes(lowerQuery) || lowerKey.includes(lowerQuery)) score = 60;
        // Keyword match
        else if (page.keywords.some(kw => kw.includes(lowerQuery))) score = 50;
        
        return { ...page, score, type: 'page' };
      })
      .filter(page => page.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
  }, []);

  // Search quick actions
  const searchQuickActions = useCallback((searchQuery) => {
    if (!searchQuery.trim()) return QUICK_ACTIONS.slice(0, 3).map(a => ({ ...a, type: 'action' }));
    
    const lowerQuery = searchQuery.toLowerCase();
    
    return QUICK_ACTIONS
      .filter(action => action.label.toLowerCase().includes(lowerQuery))
      .map(action => ({ ...action, type: 'action' }))
      .slice(0, 5);
  }, []);

  // Search entities (debounced API call)
  const searchEntities = useCallback(async (searchQuery) => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setEntityResults({ members: [], bookings: [], volunteers: [], calls: [] });
      return;
    }
    
    setIsSearching(true);
    
    try {
      const results = await SearchService.searchAll(searchQuery, activeFilter);
      setEntityResults(results);
    } catch (error) {
      console.error('Search error:', error);
      setEntityResults({ members: [], bookings: [], volunteers: [], calls: [] });
    } finally {
      setIsSearching(false);
    }
  }, [activeFilter]);

  // Handle query change with debounce for API calls
  const handleQueryChange = useCallback((newQuery) => {
    setQuery(newQuery);
    setSelectedIndex(0);
    
    // Check for quick note prefix
    if (newQuery.startsWith('note:')) {
      return;
    }
    
    // Parse NLP command
    const command = parseNLPCommand(newQuery);
    setParsedCommand(command);
    
    // Get command suggestions
    const suggestions = getCommandSuggestions(newQuery);
    setCommandSuggestions(suggestions);
    
    // Debounce entity search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      searchEntities(newQuery);
    }, 300);
  }, [searchEntities]);

  // Get all results combined
  const getAllResults = useCallback(() => {
    const results = [];
    
    // If no query, show recent pages and quick actions
    if (!query.trim()) {
      if (recentPages.length > 0) {
        results.push({ type: 'section', label: 'Recently Visited' });
        recentPages.forEach(page => results.push({ ...page, type: 'recent' }));
      }
      
      results.push({ type: 'section', label: 'Quick Actions' });
      QUICK_ACTIONS.slice(0, 4).forEach(action => results.push({ ...action, type: 'action' }));
      
      if (searchHistory.length > 0) {
        results.push({ type: 'section', label: 'Recent Searches' });
        searchHistory.slice(0, 3).forEach(item => results.push({ ...item, type: 'history' }));
      }
      
      return results;
    }
    
    // Command suggestions
    if (commandSuggestions.length > 0 && query.length >= 2) {
      results.push({ type: 'section', label: 'Suggestions' });
      commandSuggestions.slice(0, 3).forEach(sug => 
        results.push({ key: sug, label: sug, type: 'suggestion' })
      );
    }
    
    // Pages
    const pages = searchPages(query);
    if ((activeFilter === 'all' || activeFilter === 'pages') && pages.length > 0) {
      results.push({ type: 'section', label: 'Pages' });
      pages.forEach(page => results.push(page));
    }
    
    // Quick actions
    const actions = searchQuickActions(query);
    if ((activeFilter === 'all' || activeFilter === 'actions') && actions.length > 0) {
      results.push({ type: 'section', label: 'Quick Actions' });
      actions.forEach(action => results.push(action));
    }
    
    // Members
    if ((activeFilter === 'all' || activeFilter === 'members') && entityResults.members.length > 0) {
      results.push({ type: 'section', label: 'Members' });
      entityResults.members.forEach(member => results.push({ ...member, type: 'member' }));
    }
    
    // Bookings
    if ((activeFilter === 'all' || activeFilter === 'bookings') && entityResults.bookings.length > 0) {
      results.push({ type: 'section', label: 'Bookings' });
      entityResults.bookings.forEach(booking => results.push({ ...booking, type: 'booking' }));
    }
    
    // Volunteers
    if ((activeFilter === 'all' || activeFilter === 'volunteers') && entityResults.volunteers.length > 0) {
      results.push({ type: 'section', label: 'Volunteers' });
      entityResults.volunteers.forEach(vol => results.push({ ...vol, type: 'volunteer' }));
    }
    
    // Calls
    if ((activeFilter === 'all' || activeFilter === 'calls') && entityResults.calls.length > 0) {
      results.push({ type: 'section', label: 'Calls' });
      entityResults.calls.forEach(call => results.push({ ...call, type: 'call' }));
    }
    
    return results;
  }, [query, recentPages, searchHistory, commandSuggestions, activeFilter, entityResults, searchPages, searchQuickActions]);

  // Execute selected result
  const executeResult = useCallback((result) => {
    if (!result || result.type === 'section') return;
    
    // Save to search history
    if (query.trim()) {
      const historyItem = { query, timestamp: Date.now() };
      setSearchHistory(prev => {
        const filtered = prev.filter(h => h.query !== query);
        const updated = [historyItem, ...filtered].slice(0, 10);
        phiStorage?.setItem(SEARCH_HISTORY_KEY, JSON.stringify(updated));
        return updated;
      });
    }
    
    closeSearch();
    
    switch (result.type) {
      case 'page':
      case 'recent':
        navigate(result.path);
        break;
      case 'action':
        if (result.action === 'navigate') {
          navigate(result.path);
        }
        break;
      case 'member':
        navigate(`/admin/member-records/${result._id}/edit`);
        break;
      case 'booking':
        navigate(ROUTES.ADMIN_EVENTS);
        break;
      case 'volunteer':
        navigate(ROUTES.ADMIN_VOLUNTEER_REGISTRATIONS);
        break;
      case 'call':
        navigate(ROUTES.ADMIN_CALL_LOGS);
        break;
      case 'history':
        setQuery(result.query);
        handleQueryChange(result.query);
        setIsOpen(true);
        break;
      case 'suggestion':
        setQuery(result.label);
        handleQueryChange(result.label);
        break;
      default:
        break;
    }
  }, [query, navigate, handleQueryChange]);

  // Save quick note
  const saveQuickNote = useCallback((noteText) => {
    const note = { id: Date.now(), text: noteText, createdAt: Date.now() };
    setQuickNotes(prev => {
      const updated = [note, ...prev].slice(0, 20);
      phiStorage?.setItem(QUICK_NOTES_KEY, JSON.stringify(updated));
      return updated;
    });
    closeSearch();
  }, []);

  // Close and reset
  const closeSearch = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setSelectedIndex(0);
    setEntityResults({ members: [], bookings: [], volunteers: [], calls: [] });
    setParsedCommand(null);
    setCommandSuggestions([]);
  }, []);

  // Clear search history
  const clearSearchHistory = useCallback(() => {
    setSearchHistory([]);
    phiStorage?.removeItem(SEARCH_HISTORY_KEY);
  }, []);

  // Remove single history item
  const removeHistoryItem = useCallback((queryToRemove) => {
    setSearchHistory(prev => {
      const updated = prev.filter(h => h.query !== queryToRemove);
      phiStorage?.setItem(SEARCH_HISTORY_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const value = {
    // State
    isOpen,
    query,
    activeFilter,
    isSearching,
    entityResults,
    searchHistory,
    recentPages,
    quickNotes,
    selectedIndex,
    parsedCommand,
    commandSuggestions,
    
    // Actions
    setIsOpen,
    setQuery: handleQueryChange,
    setActiveFilter,
    setSelectedIndex,
    executeResult,
    closeSearch,
    saveQuickNote,
    clearSearchHistory,
    removeHistoryItem,
    getAllResults,
    
    // Data
    pages: PAGES,
    quickActions: QUICK_ACTIONS
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

export default SearchContext;
